"use client";

import React from "react";
import { CircleQuestionMark as HelpCircle, PhoneCall, ShieldCheck, Clock, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqItems = [{
  id: "item-1",
  question: "What service areas do you cover around Jonesboro?",
  answer: "We provide emergency and scheduled plumbing services throughout Jonesboro, GA, and surrounding Clayton County communities, including Morrow, Riverdale, Forest Park, Stockbridge, and Lovejoy."
}, {
  id: "item-2",
  question: "How fast can an emergency plumber arrive at my property?",
  answer: "Our dispatch team operates locally in Jonesboro. For urgent calls like burst pipes or sewer backups, our average response window is 30 to 60 minutes depending on your exact location and traffic conditions."
}, {
  id: "item-3",
  question: "Are your emergency plumbing services available 24/7?",
  answer: "Yes. We are on call 24 hours a day, 7 days a week, 365 days a year. Plumbing disasters do not stick to business hours, so our certified technicians are always ready to mobilize."
}, {
  id: "item-4",
  question: "What types of emergency plumbing repairs do you handle?",
  answer: "We handle burst pipe repairs, active water leaks, severe drain blockages, water heater breakdowns, overflowing toilets, sewer line emergencies, and main water shut-off issues."
}, {
  id: "item-5",
  question: "How do I request immediate emergency plumbing service?",
  answer: "For urgent emergencies, call our direct 24/7 phone line right away for immediate dispatch. You can also submit an urgent service request on our website if you cannot make a call."
}, {
  id: "item-6",
  question: "How do you ensure service reliability and transparent pricing?",
  answer: "All technicians are state-licensed, insured, and background-checked. We provide clear, upfront quotes before any work begins with no hidden surprise fees, backed by our workmanship satisfaction guarantee."
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="faqaccordion" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="flex flex-col items-center text-center mb-12 lg:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider gap-1.5">
            <HelpCircle className="size-3.5 text-accent" />
            <span>Got Questions?</span>
          </Badge>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight max-w-2xl">
            Frequently Asked Questions
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl">
            Find quick answers about our Jonesboro response times, service coverage, and 24/7 emergency repair support.
          </p>
        </div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="bg-card text-card-foreground border border-border rounded-xl p-6 sm:p-8 md:p-10 shadow-sm">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-2">
            {faqItems.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="border-b border-border py-1 last:border-b-0">
                <AccordionTrigger className="text-left text-base sm:text-lg font-semibold text-foreground hover:no-underline hover:text-primary transition-colors py-4">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm sm:text-base text-muted-foreground leading-relaxed pt-1 pb-4">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        <div className="mt-12 bg-secondary text-secondary-foreground rounded-xl p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 border border-border">
          <div className="flex items-start gap-4 text-left">
            <div className="p-3 bg-primary text-primary-foreground rounded-lg shrink-0 mt-0.5">
              <Clock className="size-6" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-default font-semibold text-secondary-foreground">
                Still have an unresolved plumbing issue?
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Our technicians are ready to take your call and dispatch immediately across Jonesboro, GA.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium">
              <a href="tel:4708479076" aria-label="Call Emergency Plumbing Hotline">
                <PhoneCall className="size-4 mr-2" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/contact/">
                <span>Contact Page</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-center gap-6 text-xs sm:text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="size-4 text-primary" />
            <span>Licensed & Insured Plumbers</span>
          </div>
          <span className="text-border font-bold">/</span>
          <div className="flex items-center gap-1.5">
            <Clock className="size-4 text-primary" />
            <span>Average 30-60 Min Response</span>
          </div>
        </div>
      </div>
    </section>;
}